import dayjs from '~/timeInit';

import { InventoryEntry } from '~/types/tours/inventory';

import {
  ConfigurationOrder,
  ScalePricingItem,
  TimelinePoint,
  configurationKey,
  configurationLabel,
  indexPricing,
  isSingleRoom,
  rateAtPax,
  upgradeTravellersByBase,
} from './departureInventory';

export interface DailyCostPoint {
  time: number;
  balances: Record<string, { cost: number | null; pax: number }>;
}

export interface ConfigurationSeries {
  id: string;
  label: string;
  season: string | null;
}

/** Travellers by the tour that sold them, drawn beside the per-configuration totals. */
export interface TourSplit {
  keyOf: (entry: InventoryEntry) => string;
  tours: Array<{ key: string; label: string; colour: string }>;
}

export interface TourSeries {
  id: string;
  label: string;
  colour: string;
}

const pairKey = (tour: string, option: string) => JSON.stringify([tour.toLowerCase(), option.toLowerCase()]);

/** Closing balances per configuration and local day, including an initial zero baseline. */
export function buildDailyCostPoints(
  timeline: Array<TimelinePoint>,
  items: Array<ScalePricingItem>,
  currency: string,
  season: string | null = null,
  split?: TourSplit,
  order?: ConfigurationOrder,
): { points: Array<DailyCostPoint>; series: Array<ConfigurationSeries>; tourSeries: Array<TourSeries> } {
  const movements = timeline.slice().sort((a, b) => dayjs(a.occurredAt).valueOf() - dayjs(b.occurredAt).valueOf());
  const { byConfiguration, seasonUsed } = indexPricing(items, season, currency);
  const inventorySeason = season?.trim() || seasonUsed;
  const configurations = new Map<string, { tour: string; option: string }>();
  Array.from(byConfiguration.values()).forEach((item) =>
    configurations.set(pairKey(item.tourConfiguration, item.packageConfiguration), {
      tour: item.tourConfiguration,
      option: item.packageConfiguration,
    }),
  );
  movements.forEach(({ entry }) =>
    configurations.set(pairKey(entry.tourConfiguration, entry.packageConfiguration), {
      tour: entry.tourConfiguration,
      option: entry.packageConfiguration,
    }),
  );
  const entries = Array.from(configurations.entries()).sort(([a], [b]) => a.localeCompare(b));
  const series = entries.map(([, config], index) => ({
    id: 'configuration' + index,
    label: configurationLabel(config.tour, config.option),
    season: inventorySeason,
  }));
  const tourSeries = (split?.tours ?? []).map((tour, index) => ({
    id: 'tour' + index,
    label: tour.label,
    colour: tour.colour,
  }));
  if (!movements.length) {
    return { points: [], series, tourSeries };
  }

  const counts = new Map<string, { pax: number; singles: number }>();
  const tourCounts = new Map<string, number>();
  const snapshot = (time: number): DailyCostPoint => {
    const upgrades = upgradeTravellersByBase(
      entries.map(([key, config]) => ({
        tourConfiguration: config.tour,
        packageConfiguration: config.option,
        units: counts.get(key)?.pax ?? 0,
        singles: counts.get(key)?.singles ?? 0,
      })),
      // Every drawn option, priced or not, so an inventory-only chart still stacks.
      new Set(entries.map(([, config]) => configurationKey(config.tour, config.option))),
      order,
    );
    const configurationBalances = entries.map(([key, config], index): [string, DailyCostPoint['balances'][string]] => {
      const { pax, singles } = counts.get(key) ?? { pax: 0, singles: 0 };
      const pricing = byConfiguration.get(configurationKey(config.tour, config.option));
      const upgrade = upgrades.get(configurationKey(config.tour, config.option));
      const bought = pax + (upgrade?.units ?? 0);
      const boughtSingles = singles + (upgrade?.singles ?? 0);
      const rate = pricing ? rateAtPax(pricing.scale, bought).unitPrice : null;
      const cost = bought === 0 ? 0 : rate === null ? null : bought * rate + boughtSingles * pricing.singleSupplement;
      return [series[index].id, { cost, pax: bought }];
    });
    const tourBalances = (split?.tours ?? []).map((tour, index): [string, DailyCostPoint['balances'][string]] => [
      tourSeries[index].id,
      { cost: null, pax: tourCounts.get(tour.key) ?? 0 },
    ]);

    return { time, balances: Object.fromEntries([...configurationBalances, ...tourBalances]) };
  };
  const firstDay = dayjs(movements[0].occurredAt).local().startOf('day');
  const points = [snapshot(firstDay.subtract(1, 'day').valueOf())];
  let day = firstDay.valueOf();
  movements.forEach((point) => {
    const nextDay = dayjs(point.occurredAt).local().startOf('day').valueOf();
    while (day < nextDay) {
      points.push(snapshot(day));
      day = dayjs(day).add(1, 'day').startOf('day').valueOf();
    }
    const key = pairKey(point.entry.tourConfiguration, point.entry.packageConfiguration);
    const previous = counts.get(key) ?? { pax: 0, singles: 0 };
    const change = point.entry.operation === 'allocated' ? 1 : -1;
    counts.set(key, {
      pax: previous.pax + change,
      singles: previous.singles + (isSingleRoom(point.entry.passenger.roomType) ? change : 0),
    });
    if (split) {
      const tourKey = split.keyOf(point.entry);
      tourCounts.set(tourKey, (tourCounts.get(tourKey) ?? 0) + change);
    }
  });
  points.push(snapshot(day));
  return { points, series, tourSeries };
}
